import { createContext, useContext, useEffect } from "react";

// Only populated by the build-time renderer. Client SEO behavior stays unchanged.
export const PrerenderSeoContext = createContext(null);

const PRODUCTION_ORIGIN = "https://cleansweeps.app";
const DEFAULT_SEO = {
  title: "Clean Sweeps | Shopify Sweepstakes App",
  description:
    "Clean Sweeps is a Shopify sweepstakes platform and app to run and manage sweepstakes with purchase-based entry rules, AMOE compliance, referrals, and storefront widgets.",
  path: "/",
  image: "/og/home.png",
  imageAlt: "Clean Sweeps Shopify sweepstakes overview",
  type: "website",
};

const resolveUrl = (origin, value) => {
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  if (value.startsWith("/")) return `${origin}${value}`;
  return `${origin}/${value}`;
};

const normalizeSchemaItems = (schema) => {
  if (!schema) return [];
  const items = Array.isArray(schema) ? schema : [schema];
  return items
    .filter((item) => item && typeof item === "object")
    .map((item) => {
      if (!("@context" in item)) return item;
      const { "@context": _context, ...rest } = item;
      return rest;
    });
};

const Seo = ({
  title = DEFAULT_SEO.title,
  description = DEFAULT_SEO.description,
  path = DEFAULT_SEO.path,
  image = DEFAULT_SEO.image,
  imageAlt = DEFAULT_SEO.imageAlt,
  type = DEFAULT_SEO.type,
  schema = null,
  robots = "index, follow",
}) => {
  const collectSeo = useContext(PrerenderSeoContext);
  if (collectSeo) collectSeo({ title, description, path, image, imageAlt, type, schema, robots });

  useEffect(() => {
    const origin =
      process.env.NODE_ENV === "production"
        ? PRODUCTION_ORIGIN
        : window.location.origin;
    const pageUrl = resolveUrl(origin, path);
    const imageUrl = resolveUrl(origin, image);
    const ownerId = `seo:${path}`;

    const setMetaTag = (attrName, attrValue, content) => {
      let element = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    const setLinkTag = (rel, href) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        document.head.appendChild(element);
      }
      element.setAttribute("href", href);
    };

    const applySeo = ({
      pageTitle,
      pageDescription,
      pageUrlValue,
      imageUrlValue,
      imageAltValue,
      pageType,
      extraSchema,
      pageRobots = "index, follow",
    }) => {
      document.title = pageTitle;
      document.documentElement.dataset.seoOwner = ownerId;

      setMetaTag("name", "description", pageDescription);
      setMetaTag("name", "robots", pageRobots);
      setMetaTag("property", "og:site_name", "Clean Sweeps");
      setMetaTag("property", "og:title", pageTitle);
      setMetaTag("property", "og:description", pageDescription);
      setMetaTag("property", "og:type", pageType);
      setMetaTag("property", "og:url", pageUrlValue);
      setMetaTag("property", "og:image", imageUrlValue);
      setMetaTag("property", "og:image:alt", imageAltValue);
      setMetaTag("property", "og:image:width", "1200");
      setMetaTag("property", "og:image:height", "630");
      setMetaTag("name", "twitter:card", "summary_large_image");
      setMetaTag("name", "twitter:title", pageTitle);
      setMetaTag("name", "twitter:description", pageDescription);
      setMetaTag("name", "twitter:image", imageUrlValue);
      setMetaTag("name", "twitter:image:alt", imageAltValue);

      setLinkTag("canonical", pageUrlValue);

      const baseStructuredData = {
        "@type": "WebPage",
        "@id": `${pageUrlValue}#webpage`,
        name: pageTitle,
        url: pageUrlValue,
        description: pageDescription,
        isPartOf: {
          "@type": "WebSite",
          "@id": `${origin}/#website`,
          name: "Clean Sweeps",
          url: origin,
        },
        publisher: {
          "@type": "Organization",
          "@id": `${origin}/#organization`,
          name: "Clean Sweeps LLC",
          url: origin,
          logo: {
            "@type": "ImageObject",
            url: resolveUrl(origin, "/logo512.png"),
          },
        },
        image: imageUrlValue,
      };
      const extraSchemaItems = normalizeSchemaItems(extraSchema);
      const structuredData =
        extraSchemaItems.length > 0
          ? {
              "@context": "https://schema.org",
              "@graph": [baseStructuredData, ...extraSchemaItems],
            }
          : {
              "@context": "https://schema.org",
              ...baseStructuredData,
            };

      const scriptId = "ld-json-webpage";
      let scriptEl = document.getElementById(scriptId);
      if (!scriptEl) {
        scriptEl = document.createElement("script");
        scriptEl.id = scriptId;
        scriptEl.type = "application/ld+json";
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(structuredData);
    };

    applySeo({
      pageTitle: title,
      pageDescription: description,
      pageUrlValue: pageUrl,
      imageUrlValue: imageUrl,
      imageAltValue: imageAlt,
      pageType: type,
      extraSchema: schema,
      pageRobots: robots,
    });

    return () => {
      if (document.documentElement.dataset.seoOwner !== ownerId) return;

      applySeo({
        pageTitle: DEFAULT_SEO.title,
        pageDescription: DEFAULT_SEO.description,
        pageUrlValue: resolveUrl(origin, DEFAULT_SEO.path),
        imageUrlValue: resolveUrl(origin, DEFAULT_SEO.image),
        imageAltValue: DEFAULT_SEO.imageAlt,
        pageType: DEFAULT_SEO.type,
        extraSchema: null,
      });
    };
  }, [description, image, imageAlt, path, title, type, schema, robots]);

  return null;
};

export default Seo;
export { PRODUCTION_ORIGIN, resolveUrl, normalizeSchemaItems };
